if (!customElements.get('rd-inline-quick-add')) {
  customElements.define(
    'rd-inline-quick-add',
    class RdInlineQuickAdd extends HTMLElement {
      constructor() {
        super();
        this.cleanupCallbacks = [];
        this.pendingVariantId = null;
        this.pendingTimeout = null;
        this.onSubmit = this.onSubmit.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onCartError = this.onCartError.bind(this);
        this.onEscape = this.onEscape.bind(this);
        this.onPickerToggle = this.onPickerToggle.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onSheetOutside = this.onSheetOutside.bind(this);
        this.onSheetKeydown = this.onSheetKeydown.bind(this);
        this.onSheetResize = this.onSheetResize.bind(this);
      }

      connectedCallback() {
        this.status = this.querySelector('[data-rd-inline-status]');
        this.error = this.querySelector('.rd-inline-quick-add__error');
        this.picker = this.querySelector('[data-rd-inline-picker]');
        this.summary = this.querySelector('[data-rd-inline-trigger]');
        this.forms = [...this.querySelectorAll('form')];
        this.sizeButtons = [...this.querySelectorAll('[data-rd-inline-size]')];
        this.sizeButtons.forEach((button) => {
          button.dataset.rdInlineAvailable ??= String(!button.disabled);
        });

        this.forms.forEach((form) => {
          form.addEventListener('submit', this.onSubmit, true);
          this.cleanupCallbacks.push(() => form.removeEventListener('submit', this.onSubmit, true));
        });

        this.sizeButtons.forEach((button) => {
          const onClick = () => this.selectSize(button);
          button.addEventListener('click', onClick);
          this.cleanupCallbacks.push(() => button.removeEventListener('click', onClick));
        });

        if (this.picker) {
          this.picker.addEventListener('keydown', this.onEscape);
          this.picker.addEventListener('toggle', this.onPickerToggle);
          document.addEventListener('scroll', this.onViewportChange, true);
          window.addEventListener('resize', this.onViewportChange);
          const onTrigger = (event) => {
            if (!this.picker.showPopover) {
              event.preventDefault();
              window.location.assign(this.summary.dataset.productUrl);
            }
          };
          this.summary.addEventListener('click', onTrigger);
          this.cleanupCallbacks.push(
            () => this.picker.removeEventListener('keydown', this.onEscape),
            () => this.picker.removeEventListener('toggle', this.onPickerToggle),
            () => document.removeEventListener('scroll', this.onViewportChange, true),
            () => window.removeEventListener('resize', this.onViewportChange),
            () => this.summary.removeEventListener('click', onTrigger)
          );
        }

        // Colour picker: a sheet inside the card with colour swatches, then sizes for that colour.
        this.sheet = this.querySelector('[data-rd-inline-sheet]');
        if (this.sheet && this.summary) {
          this.colourButtons = [...this.sheet.querySelectorAll('[data-rd-inline-colour]')];
          this.colourGroups = [...this.sheet.querySelectorAll('[data-rd-inline-colour-sizes]')];
          this.colourName = this.sheet.querySelector('[data-rd-inline-colour-name]');
          // The card's own colour (or the first in stock); each opening starts there.
          this.defaultColour = this.colourButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.rdInlineColour;
          const onSheetTrigger = (event) => {
            event.preventDefault();
            if (this.isSheetOpen()) this.closeSheet(true);
            else this.openSheet();
          };
          const onClose = () => this.closeSheet(true);
          const closeButton = this.sheet.querySelector('[data-rd-inline-close]');
          this.summary.addEventListener('click', onSheetTrigger);
          closeButton?.addEventListener('click', onClose);
          this.cleanupCallbacks.push(
            () => this.summary.removeEventListener('click', onSheetTrigger),
            () => closeButton?.removeEventListener('click', onClose)
          );
          this.colourButtons.forEach((button) => {
            const onColour = () => this.selectColour(button.dataset.rdInlineColour);
            button.addEventListener('click', onColour);
            this.cleanupCallbacks.push(() => button.removeEventListener('click', onColour));
          });
          // Fetch the swatch photos once the card is near the screen, so they are ready when the sheet opens.
          const swatchImages = [...this.sheet.querySelectorAll('img[loading="lazy"]')];
          if (swatchImages.length && 'IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              swatchImages.forEach((image) => (image.loading = 'eager'));
              observer.disconnect();
            }, { rootMargin: '300px 0px' });
            observer.observe(this.closest('.rd3-card') || this);
            this.cleanupCallbacks.push(() => observer.disconnect());
          }
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
        }
      }

      disconnectedCallback() {
        this.closePicker();
        this.closeSheet();
        this.cleanupCallbacks.forEach((cleanup) => cleanup());
        this.cleanupCallbacks = [];
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.clearPendingTimeout();
      }

      selectSize(button) {
        const form = this.querySelector('form');
        const variantInput = form?.querySelector('[name="id"]');
        const submitButton = form?.querySelector('[type="submit"]');
        if (!variantInput || !submitButton || button.disabled || this.pendingVariantId) return;

        this.sizeButtons.forEach((sizeButton) => sizeButton.setAttribute('aria-pressed', sizeButton === button));
        variantInput.value = button.dataset.rdInlineVariantId;
        submitButton.disabled = false;
        submitButton.setAttribute('aria-disabled', 'false');
        this.clearError();
        submitButton.click();
        if (this.pendingVariantId) this.setSizeButtonsDisabled(true);
      }

      onSubmit(event) {
        if (this.pendingVariantId) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        const form = event.currentTarget;
        const variantInput = form.querySelector('[name="id"]');
        const submitButton = form.querySelector('[type="submit"]');
        if (!variantInput?.value || !submitButton || submitButton.disabled) return;

        this.pendingVariantId = String(variantInput.value);
        this.setStatus('Adding…');
        this.clearError();
        this.clearPendingTimeout();
        this.pendingTimeout = window.setTimeout(() => {
          if (this.pendingVariantId) {
            this.setStatus('Check cart');
          }
        }, 20000);
      }

      onCartUpdate(event) {
        if (
          event?.source !== 'product-form' ||
          !this.pendingVariantId ||
          String(event.productVariantId) !== this.pendingVariantId
        ) {
          return;
        }

        this.pendingVariantId = null;
        this.clearPendingTimeout();
        this.setStatus('Added');
        this.setSizeButtonsDisabled(false);

        if (this.picker || this.sheet) {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          cart?.setActiveElement(this.summary);
          if (this.picker) this.closePicker(true);
          if (this.sheet) this.closeSheet();
        }
      }

      onCartError(event) {
        if (
          event?.source !== 'product-form' ||
          !this.pendingVariantId ||
          String(event.productVariantId) !== this.pendingVariantId
        ) {
          return;
        }

        this.pendingVariantId = null;
        this.clearPendingTimeout();
        this.setStatus('Unable to add');
        this.setSizeButtonsDisabled(false);

        const message = event.message || (typeof event.errors === 'string' ? event.errors : '');
        if (message && this.error) {
          const errorMessage = this.error.querySelector('.product-form__error-message');
          if (errorMessage) errorMessage.textContent = message;
          this.error.hidden = false;
        }
      }

      isPickerOpen() {
        return !!this.picker?.showPopover && this.picker.matches(':popover-open');
      }

      closePicker(returnFocus = false) {
        if (this.isPickerOpen()) this.picker.hidePopover();
        this.summary?.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.summary?.focus({ preventScroll: true });
      }

      onEscape(event) {
        if (event.key !== 'Escape' || !this.isPickerOpen()) return;
        event.preventDefault();
        event.stopPropagation();
        this.closePicker(true);
      }

      onViewportChange(event) {
        // Close when this card moves; unrelated carousels must not dismiss its picker.
        if (!this.isPickerOpen()) return;
        if (event.target instanceof Node && this.picker.contains(event.target)) return;
        if (event.type === 'scroll' && event.target instanceof Node && !event.target.contains(this)) return;
        this.closePicker();
      }

      onPickerToggle() {
        const open = this.isPickerOpen();
        this.summary?.setAttribute('aria-expanded', String(open));
        if (!open) return;
        this.positionPicker();
        this.sizeButtons.find((button) => !button.disabled)?.focus({ preventScroll: true });
      }

      positionPicker() {
        const anchor = this.summary.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = document.documentElement.clientWidth;
        const gap = 8;
        const edge = 12;
        this.picker.style.maxHeight = `${Math.max(80, viewportHeight - edge * 2)}px`;
        const panel = this.picker.getBoundingClientRect();
        const top = anchor.bottom + gap + panel.height <= viewportHeight - edge
          ? anchor.bottom + gap
          : Math.max(edge, anchor.top - gap - panel.height);
        const left = Math.max(edge, Math.min(anchor.right - panel.width, viewportWidth - panel.width - edge));
        this.picker.style.top = `${top}px`;
        this.picker.style.left = `${left}px`;
      }

      isSheetOpen() {
        return !!this.sheet && !this.sheet.hidden;
      }

      openSheet() {
        if (!this.sheet || this.isSheetOpen()) return;
        // One picker at a time across the page.
        document.querySelectorAll('rd-inline-quick-add').forEach((other) => {
          if (other !== this && typeof other.closeSheet === 'function') other.closeSheet();
        });
        if (this.defaultColour) this.selectColour(this.defaultColour);
        this.sheet.hidden = false;
        this.classList.add('rd-inline-quick-add--open');
        this.closest('.rd3-card')?.classList.add('rd3-card--picking');
        this.summary.setAttribute('aria-expanded', 'true');
        this.setStatus('');
        this.clearError();
        document.addEventListener('pointerdown', this.onSheetOutside, true);
        document.addEventListener('keydown', this.onSheetKeydown);
        window.addEventListener('resize', this.onSheetResize);
        this.layoutSizes();
        const group = this.activeColourGroup();
        const target =
          group?.querySelector('[data-rd-inline-popular]:not([disabled])') ||
          group?.querySelector('[data-rd-inline-size]:not([disabled])') ||
          this.colourButtons.find((button) => button.getAttribute('aria-pressed') === 'true');
        target?.focus({ preventScroll: true });
      }

      closeSheet(returnFocus = false) {
        if (!this.sheet) return;
        const wasOpen = this.isSheetOpen();
        this.sheet.hidden = true;
        this.classList.remove('rd-inline-quick-add--open');
        this.closest('.rd3-card')?.classList.remove('rd3-card--picking');
        this.summary?.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', this.onSheetOutside, true);
        document.removeEventListener('keydown', this.onSheetKeydown);
        window.removeEventListener('resize', this.onSheetResize);
        if (wasOpen && returnFocus) this.summary?.focus({ preventScroll: true });
      }

      activeColourGroup() {
        return this.colourGroups?.find((group) => !group.hidden) || null;
      }

      selectColour(colour) {
        if (!this.sheet || this.pendingVariantId) return;
        this.colourButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.rdInlineColour === colour));
        });
        this.colourGroups.forEach((group) => {
          group.hidden = group.dataset.rdInlineColourSizes !== colour;
        });
        if (this.colourName) this.colourName.textContent = colour;
        this.clearError();
        this.setStatus('');
        if (this.isSheetOpen()) this.layoutSizes();
      }

      // Even rows like the product page: the most columns that divide the sizes and stay at least 40px wide.
      layoutSizes() {
        const group = this.activeColourGroup();
        if (!group) return;
        const count = group.querySelectorAll('[data-rd-inline-size]').length;
        const width = group.clientWidth;
        if (!count || !width) return;
        const gap = parseFloat(getComputedStyle(group).columnGap) || 5;
        let columns = count;
        while (columns > 1 && (width - gap * (columns - 1)) / columns < 40) {
          do {
            columns -= 1;
          } while (columns > 1 && count % columns !== 0);
        }
        group.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
      }

      onSheetResize() {
        if (this.isSheetOpen()) this.layoutSizes();
      }

      onSheetOutside(event) {
        if (!this.isSheetOpen()) return;
        const target = event.target;
        if (target instanceof Node && (this.sheet.contains(target) || this.summary.contains(target))) return;
        this.closeSheet();
      }

      onSheetKeydown(event) {
        if (event.key !== 'Escape' || !this.isSheetOpen()) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeSheet(true);
      }

      clearError() {
        if (!this.error) return;

        this.error.hidden = true;
        const errorMessage = this.error.querySelector('.product-form__error-message');
        if (errorMessage) errorMessage.textContent = '';
      }

      setSizeButtonsDisabled(disabled) {
        this.sizeButtons.forEach((button) => {
          button.disabled = disabled || button.dataset.rdInlineAvailable !== 'true';
        });
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      clearPendingTimeout() {
        if (this.pendingTimeout) window.clearTimeout(this.pendingTimeout);
        this.pendingTimeout = null;
      }
    }
  );
}
